import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { CheckoutCard, type CheckoutIntent } from '@/components/checkout/CheckoutCard';
import { CheckoutModal } from '@/components/checkout/CheckoutModal';

const AUTO_OPEN_KEY = 'brew.checkout.autoOpened';
const TITLE_ID = 'rcpt-title-modal';

/**
 * The in-app nudge for a user with a PENDING onboarding intent (chose a paid
 * plan at signup but hasn't paid). Replaces the old full-width gold TOP STRIP —
 * which shifted the whole layout — with:
 *   • a creative, accessible CheckoutModal (the shared "statement" card), and
 *   • a small inline ResumeChip (top-right, position:fixed) that never reflows
 *     the message list and re-opens the modal any time.
 *
 * The modal auto-opens AT MOST once per browser session (sessionStorage flag),
 * and never auto-opens if the intent was already snoozed server-side. Renders
 * nothing when there's no open intent (or the fetch 401s for a guest).
 */
export function OnboardingBanner() {
  const navigate = useNavigate();
  const [intent, setIntent] = useState<CheckoutIntent | null>(null);
  const [open, setOpen] = useState(false);
  const [acting, setActing] = useState(false);
  const settleTimer = useRef<number | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get<{ intent: CheckoutIntent | null }>('/api/v2/onboarding/intent');
        if (!alive || !r.intent?.plan) return;
        setIntent(r.intent);
        // Auto-open once per session — but not if already snoozed, and after a
        // short settle so it doesn't fight the chat's first paint.
        const already = sessionStorage.getItem(AUTO_OPEN_KEY);
        if (!already && !r.intent.dismissedAt) {
          settleTimer.current = window.setTimeout(() => {
            sessionStorage.setItem(AUTO_OPEN_KEY, '1');
            setOpen(true);
          }, 600);
        }
      } catch { /* not logged in / no intent — silent */ }
    })();
    return () => {
      alive = false;
      if (settleTimer.current) window.clearTimeout(settleTimer.current);
    };
  }, []);

  if (!intent?.plan) return null;
  const { plan, coupon, offerExpired } = intent;

  function payNow() {
    setActing(true);
    const q = new URLSearchParams({ plan: plan.name });
    if (coupon && !offerExpired) q.set('coupon', coupon.code);
    navigate(`/plans?${q.toString()}`);
  }

  // "Maybe later" — snooze server-side (longer window), keep the chip for later.
  async function payLater() {
    setOpen(false);
    try { await api.post('/api/v2/onboarding/intent/snooze', {}); } catch { /* best-effort */ }
  }

  return (
    <>
      {!open && (
        <button type="button" className="resume-chip" onClick={() => setOpen(true)}>
          <span className="resume-chip__dot" aria-hidden="true" />
          Complete your {plan.name} purchase
        </button>
      )}
      {open && (
        <CheckoutModal titleId={TITLE_ID} onClose={() => setOpen(false)}>
          <CheckoutCard
            plan={plan}
            coupon={coupon}
            offerExpired={offerExpired}
            onPayNow={payNow}
            onSecondary={payLater}
            secondaryLabel="Maybe later"
            busy={acting}
            titleId={TITLE_ID}
          />
        </CheckoutModal>
      )}
    </>
  );
}
